import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { Button, InputBoxSingle } from '@peacecover/component';
import useUserQuery from '../hooks/queries/useUserQuery';
import StickyHeader from '../components/bar/StickyHeader';
import {
  isBillingBuyerInfoComplete,
  loadBillingBuyerInfo,
  saveBillingBuyerInfo
} from '../../utils/billingBuyerInfoStorage';
import { getOneTimePaymentLaunchPath, getPgProvider } from '../../utils/paymentProvider';
import PayLetterPaymentMethodSelector from './components/PayLetterPaymentMethodSelector';

const PaymentBuyerInfoPage = () => {
  const navigate = useNavigate();
  const { data: user } = useUserQuery();
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const amount = searchParams.get('amount') ?? '';
  const productName = searchParams.get('productName') ?? '';
  const productId = searchParams.get('productId') ?? '';
  const isBilling = searchParams.get('isBilling') === 'true';
  const productType = searchParams.get('productType') ?? '';
  const sceneId = searchParams.get('sceneId') ?? '';
  const autoPay = searchParams.get('autoPay') === 'true';
  const isPayLetter = getPgProvider() === 'pay-letter';
  const shouldUsePayLetterLaunch = isPayLetter;
  const resolvedAutoPay = autoPay || (isBilling && shouldUsePayLetterLaunch);
  const shouldOpenPayLetterMethodSelector = shouldUsePayLetterLaunch && !resolvedAutoPay;

  const [form, setForm] = useState(() => {
    return (
      loadBillingBuyerInfo() ?? {
        buyerName: '',
        buyerEmail: '',
        buyerTel: ''
      }
    );
  });
  const [isPayLetterMethodSelectorOpen, setIsPayLetterMethodSelectorOpen] = useState(false);

  useEffect(() => {
    if (!user?.email) return;
    if (form.buyerEmail) return;
    setForm((prev) => ({
      ...prev,
      buyerEmail: user.email
    }));
  }, [user?.email, form.buyerEmail]);

  const isSubmitDisabled = !isBillingBuyerInfoComplete(form);

  const handleChange =
    (key: 'buyerName' | 'buyerEmail' | 'buyerTel') => (event: { target: { value: string } }) => {
      setForm((prev) => ({
        ...prev,
        [key]: event.target.value
      }));
    };

  const handleSubmit = () => {
    if (!productId || !amount || !productName) return;
    if (!isBillingBuyerInfoComplete(form)) return;
    saveBillingBuyerInfo(form);
    const nextPath =
      isBilling && !shouldUsePayLetterLaunch ? '/payment/billing' : getOneTimePaymentLaunchPath();
    if (shouldOpenPayLetterMethodSelector) {
      setIsPayLetterMethodSelectorOpen(true);
      return;
    }
    const sceneQuery = sceneId ? `&sceneId=${encodeURIComponent(sceneId)}` : '';
    navigate(
      `${nextPath}?amount=${amount}&productName=${productName}&productId=${productId}${productType ? `&productType=${encodeURIComponent(productType)}` : ''}${sceneQuery}${resolvedAutoPay ? '&autoPay=true' : ''}`
    );
  };

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-light dark:bg-background-dark">
      <StickyHeader showBackButton={true} onClickBackButton={() => window.close()} />
      <div className="pt-[52px] px-[24px] flex flex-col gap-[16px]">
        <div className="flex flex-col gap-[8px]">
          <h1 className="text-[22px] font-bold text-text-main-lv1 leading-[26px]">
            {String(t('PaymentBuyerInfo.title'))}
          </h1>
          <p className="text-[14px] font-medium text-text-main-lv4 leading-[18px] whitespace-pre-line">
            {String(t('PaymentBuyerInfo.description'))}
          </p>
        </div>
        <div className="flex flex-col gap-[12px]">
          <InputBoxSingle
            className="w-full bg-transparent"
            value={form.buyerEmail}
            onChange={handleChange('buyerEmail')}
            onClear={() => setForm((prev) => ({ ...prev, buyerEmail: '' }))}
            placeholder={String(t('PaymentBuyerInfo.emailPlaceholder'))}
            type="email"
            maxLength={1000}
          />
          <InputBoxSingle
            className="w-full bg-transparent"
            value={form.buyerName}
            onChange={handleChange('buyerName')}
            onClear={() => setForm((prev) => ({ ...prev, buyerName: '' }))}
            placeholder={String(t('PaymentBuyerInfo.namePlaceholder'))}
          />
          <InputBoxSingle
            className="w-full bg-transparent"
            value={form.buyerTel}
            onChange={handleChange('buyerTel')}
            onClear={() => setForm((prev) => ({ ...prev, buyerTel: '' }))}
            placeholder={String(t('PaymentBuyerInfo.telPlaceholder'))}
            inputMode="tel"
          />
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+10px)]">
        <Button
          size="large"
          className="w-full text-[17px] font-semibold leading-[20px] rounded-[18px]"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          다음
        </Button>
      </div>
      <PayLetterPaymentMethodSelector
        isOpen={isPayLetterMethodSelectorOpen}
        onClose={() => setIsPayLetterMethodSelectorOpen(false)}
        onSelect={(paymentMethod) => {
          const nextPath = getOneTimePaymentLaunchPath();
          setIsPayLetterMethodSelectorOpen(false);
          const sceneQuery = sceneId ? `&sceneId=${encodeURIComponent(sceneId)}` : '';
          navigate(
            `${nextPath}?amount=${amount}&productName=${productName}&productId=${productId}${productType ? `&productType=${encodeURIComponent(productType)}` : ''}${sceneQuery}&paymentMethod=${paymentMethod}${resolvedAutoPay ? '&autoPay=true' : ''}`
          );
        }}
      />
    </div>
  );
};

export default PaymentBuyerInfoPage;
